import { useState, useEffect, useRef, memo } from "react";
import { Skeleton } from "@mui/material";
import { NAIL_SITUATION_OPTIONS, CONTENT_TYPE_OPTIONS } from "../../../ActionsPanel/Actions/ChecksMenu/ManualCheck";
import TextFieldEditable from "../TextFieldEditable";
import StatusCell from "../StatusCell";
import RefusalReason from "../RefusalReasonCell";
import DefaultCell from "../DefaultCell";
import NotesCell from "../NotesCell";
import AssignedMemberCell from "../AssignedMemberCell";
import AudienceCell from "../AudienceCell";
import FollowersCell from "../FollowersCell";

const LazyCell = memo(({ 
  col, 
  row,
}) => {
  const getOptionValue = (options) => options.find(
    (el) => el.value === row[col] || el.label === row[col] || el.aliases?.includes(row[col])
  )?.value || row[col] || "n/a"
  
  // Render cell content
  const renderCellContent = (col, row) => {
    switch (col) {
      case "status":
        return <StatusCell row={row} col={col} />;
      
      case "notes":
        return <NotesCell row={row} />;
      
      case "refusal_reason":
        return <RefusalReason data={row} />;
      
      case "firstname":
        return <TextFieldEditable row={row} col={col} />;

      case "content_type_validation":
        return getOptionValue(CONTENT_TYPE_OPTIONS);
      
      case "nail_situation":
        return getOptionValue(NAIL_SITUATION_OPTIONS);
      
      case "assigned_member":
        return <AssignedMemberCell row={row} col={col} />;
      
      case "audience_countries_full":
        return <AudienceCell row={row} col={col} />;
      
      case "audience_languages_full":
        return <AudienceCell row={row} col={col} />;
      
      case "audience_cities_full":
        return <AudienceCell row={row} col={col} />;
      
      case "audience_interests_full":
        return <AudienceCell row={row} col={col} />;
      
      case "followers":
        return <FollowersCell row={row} showNumber={true} />;
      
      default:
        return <DefaultCell row={row} col={col} />;
    }
  };

  // Disable lazy loading for critical cells (status, name, followers)
  const criticalCells = ['status', 'firstname', 'username', 'followers'];
  const shouldUseLazyLoading = !criticalCells.includes(col);
  
  const [isVisible, setIsVisible] = useState(!shouldUseLazyLoading);
  const cellRef = useRef(null);

  useEffect(() => {
    if (!shouldUseLazyLoading) return;

    let timeoutId = null;

    // Simple visibility check with delay
    const checkVisibility = () => {
      if (cellRef.current) {
        const rect = cellRef.current.getBoundingClientRect();
        const isInViewport = (
          rect.top < window.innerHeight &&
          rect.bottom > 0 &&
          rect.left < window.innerWidth &&
          rect.right > 0
        );
        
        if (isInViewport) {
          setIsVisible(true);
        }
      }
    };

    // Check immediately
    checkVisibility();

    // Check with small delay for reliability
    timeoutId = setTimeout(checkVisibility, 100);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, [shouldUseLazyLoading]);

  // Simple IntersectionObserver as fallback
  useEffect(() => {
    if (!shouldUseLazyLoading) return;

    let observer = null;

    const createObserver = () => {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Disconnect observer after first trigger to save resources
            if (observer) {
              observer.disconnect();
              observer = null;
            }
          }
        },
        {
          rootMargin: "200px", // Large margin
          threshold: 0,
        }
      );
    };

    if (cellRef.current) {
      createObserver();
      observer.observe(cellRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };
  }, [shouldUseLazyLoading]);

  if (!isVisible) {
    return <Skeleton variant="text" ref={cellRef} width="80%" />;
  }

  if (row.isDetailRow && !row[col]) return null

  // Universal cell rendering logic (same for DataGrid and regular table)
  return renderCellContent(col, row);
}, (prevProps, nextProps) => {
  // Re-render component if row data or column changed
  // Use more precise comparison for optimization
  if (prevProps.col !== nextProps.col) return false;
  if (prevProps.row !== nextProps.row) return false;
  
  return true;
});

export default LazyCell;
